import React, { useState, useEffect, useRef } from 'react';
import type {
	EditWidgetRowProps,
	WidgetDataSourceProps,
	WidgetRow
} from 'App/components/hooks/integrations/externalIntegrationTypes';
import { Flex, Input, Select, Tooltip, Checkbox } from '@upsales/components';
import { useTranslation } from 'Components/Helpers/translate';
import WidgetFieldSetting from './helpers/WidgetFieldSetting';
import useExternalIntegrations from 'App/components/hooks/integrations/useExternalIntegrations';
import { removeBracketsFromTag } from './UiWidgetHelpers';
import type { Order } from 'App/resources/Model/Order';
import CodeEditor from 'App/babel/components/CodeEditor';
import { get, debounce, isEqual } from 'lodash';
import { EditUiWidgetRow } from './UiWidgetRow';
import UiWidgetRow from './UiWidgetRow';
import { handleWidgetResponse, triggerDataSource } from './helpers/widgetResponseHandler';
import { makeCancelable, CancelablePromise } from 'Helpers/promise';

type FieldActions = {
	updated?: {
		order: Partial<Order>;
	};
	disabled?: {
		order: { [key: string]: boolean | FieldActions };
	};
	visible?: {
		order: { [key: string]: boolean | FieldActions };
	};
};

export const EditUiWidgetRunOnChange = ({ row, onUpdate, dataSources }: EditWidgetRowProps) => {
	const { t } = useTranslation();
	const monitorTag = row.settings?.monitorTag || '';
	const selectedDataSourceName = row.settings?.dataSource || '';
	const enableLoop = row.settings?.enableLoop || false;
	const responseHandling = row.settings?.responseHandling || 'none';
	const responseMapping = row.settings?.responseMapping || '';
	const hideIfFieldEmpty = row.settings?.hideIfFieldEmpty || '';
	const debounceMs = row.settings?.debounceMs || 500;

	const dataSourceOptions = Object.keys(dataSources || {}).map(name => ({ id: name, title: name }));
	const selectedOption = dataSourceOptions.find(opt => opt.id === selectedDataSourceName) || null;

	const responseHandlingOptions = [
		{ id: 'none', title: t('default.responseHandlingNone') },
		{ id: 'updateOrder', title: t('default.responseHandlingUpdateOrder') },
		{ id: 'showRows', title: t('default.responseHandlingShowRows') }
	];
	const selectedResponseHandling =
		responseHandlingOptions.find(opt => opt.id === responseHandling) || responseHandlingOptions[0];

	const handleChange = (field: string, value: string | boolean | Record<string, any> | number) => {
		onUpdate({
			...row,
			settings: {
				...row.settings,
				[field]: value
			}
		});
	};

	const handleResponseMappingChange = (editorState: any) => {
		const jsonString = editorState.code;
		handleChange('responseMapping', jsonString);
	};

	return (
		<Flex direction="column" gap="u2">
			<WidgetFieldSetting text={t('default.monitorTag')}>
				<Tooltip title={t('default.monitorTagTooltip')}>
					<Input value={monitorTag} onChange={e => handleChange('monitorTag', e.target.value)} />
				</Tooltip>
			</WidgetFieldSetting>

			<WidgetFieldSetting text={t('default.debounceMs')}>
				<Tooltip title={t('default.debounceMsTooltip')}>
					<Input
						type="number"
						value={debounceMs}
						onChange={e => handleChange('debounceMs', parseInt(e.target.value) || 500)}
					/>
				</Tooltip>
			</WidgetFieldSetting>

			<WidgetFieldSetting text={t('default.dataSource')}>
				<Select
					value={selectedOption}
					options={dataSourceOptions}
					onChange={opt => handleChange('dataSource', opt?.id)}
					anchor=".Modals"
				/>
			</WidgetFieldSetting>

			<WidgetFieldSetting text={t('default.loopOrderRows')}>
				<Tooltip title={t('default.loopOrderRowsTooltip')}>
					<Checkbox checked={enableLoop} onChange={checked => handleChange('enableLoop', checked)} />
				</Tooltip>
			</WidgetFieldSetting>

			<WidgetFieldSetting text={t('default.responseHandling')}>
				<Tooltip title={t('default.responseHandlingTooltip')}>
					<Select
						value={selectedResponseHandling}
						options={responseHandlingOptions}
						onChange={opt => handleChange('responseHandling', opt?.id)}
						anchor=".Modals"
					/>
				</Tooltip>
			</WidgetFieldSetting>

			{responseHandling === 'updateOrder' ? (
				<WidgetFieldSetting text={t('default.responseMapping')}>
					<Tooltip title={t('default.responseMappingTooltip')}>
						<CodeEditor
							value={responseMapping}
							onChange={handleResponseMappingChange}
							mode="json"
							theme="cobalt"
							maxLines={10}
							showGutter={true}
						/>
					</Tooltip>
				</WidgetFieldSetting>
			) : null}

			{responseHandling === 'showRows' ? (
				<WidgetFieldSetting text={t('default.rowConfiguration')}>
					<EditUiWidgetRow
						row={{ type: 'Row', settings: row.settings?.rowConfiguration || {} } as WidgetRow}
						onUpdate={(updatedRow: WidgetRow) => handleChange('rowConfiguration', updatedRow.settings)}
						dataSources={dataSources}
					/>
				</WidgetFieldSetting>
			) : null}

			{hideIfFieldEmpty ? (
				<WidgetFieldSetting text={t('default.hideIfFieldEmpty')}>
					<Tooltip title={t('default.hideIfFieldEmptyTooltip')}>
						<Input
							value={hideIfFieldEmpty}
							onChange={e => handleChange('hideIfFieldEmpty', e.target.value)}
						/>
					</Tooltip>
				</WidgetFieldSetting>
			) : null}
		</Flex>
	);
};

const UiWidgetRunOnChange: React.FC<
	WidgetDataSourceProps & { updateOrderFromApp?: (fieldActions: FieldActions, order?: Order) => void }
> = ({ row, widgetExternalId, dataSources, widgetSettings, dataSourceSettings, updateOrderFromApp }) => {
	const { getDataSource, user } = useExternalIntegrations();
	const [isLoading, setIsLoading] = useState(false);
	const [responseData, setResponseData] = useState<any>(null);
	const previousValueRef = useRef<any>(undefined);
	const triggerPromiseRef = useRef<CancelablePromise | null>(null);

	const monitorTag = row.settings?.monitorTag;
	const dataSourceName: string | undefined = row.settings?.dataSource;
	const enableLoop = row.settings?.enableLoop || false;
	const hideIfFieldEmpty = row.settings?.hideIfFieldEmpty;
	const responseHandling = row.settings?.responseHandling || 'none';
	const debounceMs = row.settings?.debounceMs || 500;

	const debouncedTriggerRef = useRef(
		debounce((callback: () => void) => {
			callback();
		}, debounceMs)
	);

	useEffect(() => {
		return () => {
			debouncedTriggerRef.current.cancel();
			triggerPromiseRef.current?.cancel();
		};
	}, []);

	const handleResponse = (responses: any[]) => {
		handleWidgetResponse({
			responses,
			responseHandling,
			responseMapping: row.settings?.responseMapping,
			dataSources,
			widgetExternalId,
			widgetSettings,
			user,
			updateOrderFromApp,
			setResponseData,
			errorContext: 'RunOnChange'
		});
	};

	const handleTrigger = async () => {
		if (!dataSourceName || !dataSourceSettings?.[dataSourceName]) return;
		if (isLoading) return;

		triggerPromiseRef.current?.cancel();

		const triggerPromise = makeCancelable(
			triggerDataSource({
				dataSourceName,
				dataSourceSettings,
				enableLoop,
				dataSources,
				widgetExternalId,
				user,
				getDataSource,
				handleResponse,
				setIsLoading,
				errorContext: 'RunOnChange'
			})
		);

		triggerPromiseRef.current = triggerPromise;

		try {
			await triggerPromise.promise;
		} catch (error) {
			if (!triggerPromise.isCanceled()) {
				throw error;
			}
		}
	};

	useEffect(() => {
		debouncedTriggerRef.current = debounce((callback: () => void) => {
			callback();
		}, debounceMs);
	}, [debounceMs]);

	useEffect(() => {
		if (!monitorTag || !dataSources) return;

		const fieldPath = removeBracketsFromTag(monitorTag);
		const customFieldMatch = fieldPath.match(/^(.+\.custom)\.?\[(?:'|")(.+?)(?:'|")\]$/);
		let fieldValue;
		if (customFieldMatch) {
			const basePath = customFieldMatch[1];
			const fieldName = customFieldMatch[2];
			const customFields = get(dataSources, basePath) as any[];

			if (Array.isArray(customFields)) {
				const field = customFields.find(f => f.name === fieldName);
				fieldValue = field?.value;
			}

			if (!fieldValue) {
				const mappedCustomPath = basePath.replace('.custom', '.$mappedCustom');
				const mappedCustomFields = get(dataSources, mappedCustomPath) as Record<string, any>;

				if (mappedCustomFields && typeof mappedCustomFields === 'object') {
					const matchingField = Object.values(mappedCustomFields).find(
						(field: any) => field?.name === fieldName
					);
					if (matchingField) {
						fieldValue = matchingField.value;
					}
				}
			}
		} else {
			fieldValue = get(dataSources, fieldPath);
		}

		if (hideIfFieldEmpty && !fieldValue) {
			return;
		}

		const hasChanged = !isEqual(previousValueRef.current, fieldValue);

		if (fieldValue && hasChanged) {
			previousValueRef.current = fieldValue;

			handleTrigger();
		}

		return () => {
			debouncedTriggerRef.current.cancel();
			triggerPromiseRef.current?.cancel();
		};
	}, [monitorTag, dataSources]);

	const rowConfiguration = row.settings?.rowConfiguration;

	return responseHandling === 'showRows' && responseData && rowConfiguration ? (
		<UiWidgetRow
			row={{ type: 'Row', settings: rowConfiguration } as WidgetRow}
			widgetExternalId={widgetExternalId}
			dataSources={{
				...dataSources,
				Response: responseData
			}}
			widgetSettings={widgetSettings}
			dataSourceSettings={dataSourceSettings}
		/>
	) : null;
};

export default UiWidgetRunOnChange;
